import React, { useEffect, useRef, useState } from 'react';

type TurnstileMessageType =
  | 'TURNSTILE_READY'
  | 'TURNSTILE_SUCCESS'
  | 'TURNSTILE_ERROR'
  | 'TURNSTILE_EXPIRED';

type TurnstileMessage = {
  type: TurnstileMessageType;
  token?: string;
  code?: string;
};

type TurnstileWidgetId = string;
type TurnstileTheme = 'light' | 'dark' | 'auto';
type TurnstileAppearance = 'always' | 'execute' | 'interaction-only';

type TurnstileConfig = {
  sitekey: string;
  theme: TurnstileTheme;
  appearance: TurnstileAppearance;
};

declare global {
  interface Window {
    ReactNativeWebView?: {
      postMessage: (message: string) => void;
    };
  }
}

const TURNSTILE_SCRIPT_ID = 'cloudflare-turnstile-script';
const TURNSTILE_SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const DEFAULT_THEME: TurnstileTheme = 'light';
const DEFAULT_APPEARANCE: TurnstileAppearance = 'always';

let turnstileScriptPromise: Promise<void> | undefined;

function postToReactNative(message: TurnstileMessage) {
  window.ReactNativeWebView?.postMessage(JSON.stringify(message));
}

function getTurnstileConfigFromUrl(): TurnstileConfig | null {
  const params = new URLSearchParams(window.location.search);
  const sitekey = params.get('sitekey')?.trim();

  if (!sitekey) return null;

  const theme = params.get('theme');
  const appearance = params.get('appearance');
  const allowedThemes: TurnstileTheme[] = ['light', 'dark', 'auto'];
  const allowedAppearances: TurnstileAppearance[] = [
    'always',
    'execute',
    'interaction-only',
  ];

  return {
    sitekey,
    theme: allowedThemes.includes(theme as TurnstileTheme)
      ? (theme as TurnstileTheme)
      : DEFAULT_THEME,
    appearance: allowedAppearances.includes(appearance as TurnstileAppearance)
      ? (appearance as TurnstileAppearance)
      : DEFAULT_APPEARANCE,
  };
}

function waitForTurnstileReady(timeoutMs = 10000) {
  return new Promise<void>((resolve, reject) => {
    if (window.turnstile) {
      resolve();
      return;
    }

    const startedAt = Date.now();
    const intervalId = window.setInterval(() => {
      if (window.turnstile) {
        window.clearInterval(intervalId);
        resolve();
        return;
      }

      if (Date.now() - startedAt >= timeoutMs) {
        window.clearInterval(intervalId);
        reject(new Error('TURNSTILE_API_NOT_READY'));
      }
    }, 50);
  });
}

function loadTurnstileScript() {
  if (window.turnstile) return Promise.resolve();
  if (turnstileScriptPromise) return turnstileScriptPromise;

  turnstileScriptPromise = new Promise<void>((resolve, reject) => {
    const existingScript = document.getElementById(TURNSTILE_SCRIPT_ID);
    const resolveWhenReady = () =>
      waitForTurnstileReady().then(resolve, reject);

    if (existingScript) {
      existingScript.addEventListener(
        'error',
        () => reject(new Error('TURNSTILE_SCRIPT_LOAD_FAILED')),
        { once: true },
      );
      resolveWhenReady();
      return;
    }

    const script = document.createElement('script');
    script.id = TURNSTILE_SCRIPT_ID;
    script.src = TURNSTILE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = resolveWhenReady;
    script.onerror = () => reject(new Error('TURNSTILE_SCRIPT_LOAD_FAILED'));
    document.head.appendChild(script);
  }).catch((error: Error) => {
    turnstileScriptPromise = undefined;
    throw error;
  });

  return turnstileScriptPromise;
}

const TurnstilePage = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<TurnstileWidgetId | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>(
    'loading',
  );

  useEffect(() => {
    let cancelled = false;

    const renderTurnstile = async () => {
      const config = getTurnstileConfigFromUrl();

      if (!config) {
        setStatus('error');
        postToReactNative({
          type: 'TURNSTILE_ERROR',
          code: 'MISSING_SITE_KEY',
        });
        return;
      }

      try {
        await loadTurnstileScript();

        if (cancelled || !containerRef.current) return;
        if (!window.turnstile) throw new Error('TURNSTILE_API_NOT_READY');

        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: config.sitekey,
          theme: config.theme,
          appearance: config.appearance,
          callback: (token) =>
            postToReactNative({ type: 'TURNSTILE_SUCCESS', token }),
          'error-callback': (code) => {
            setStatus('error');
            postToReactNative({ type: 'TURNSTILE_ERROR', code });
          },
          'expired-callback': () => {
            postToReactNative({ type: 'TURNSTILE_EXPIRED' });
            if (widgetIdRef.current)
              window.turnstile?.reset?.(widgetIdRef.current);
          },
          'timeout-callback': () => {
            postToReactNative({ type: 'TURNSTILE_ERROR', code: 'TIMEOUT' });
            if (widgetIdRef.current)
              window.turnstile?.reset?.(widgetIdRef.current);
          },
        });

        setStatus('ready');
        postToReactNative({ type: 'TURNSTILE_READY' });
      } catch {
        if (cancelled) return;

        setStatus('error');
        postToReactNative({
          type: 'TURNSTILE_ERROR',
          code: 'SCRIPT_LOAD_FAILED',
        });
      }
    };

    renderTurnstile();

    return () => {
      cancelled = true;
      if (widgetIdRef.current) {
        window.turnstile?.remove?.(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, []);

  return (
    <main style={{ minHeight: '100vh', backgroundColor: '#fff' }}>
      <div
        style={{
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: '0 16px',
        }}
      >
        <div
          aria-busy={status === 'loading'}
          style={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'center',
            minHeight: 96,
            minWidth: 300,
          }}
        >
          <div ref={containerRef} />
          {status === 'loading' && (
            <span style={{ color: '#6b7280', fontSize: 14 }}>
              Đang tải xác thực...
            </span>
          )}
          {status === 'error' && (
            <span role='alert' style={{ color: '#dc2626', fontSize: 14 }}>
              Không thể tải xác thực. Vui lòng thử lại.
            </span>
          )}
        </div>
      </div>
    </main>
  );
};

export default TurnstilePage;
